import { BodyText, Button } from '@farol-ds/react'
import { useEffect, useRef } from 'react'

import {
  type EntityNoticePageKind,
  type EntityNoticeVariant,
  buildEntityNoticeTerminalSubtitle,
  ENTITY_NOTICE_TERMINAL_PRIMARY_BUTTON_TEXT,
  ENTITY_NOTICE_TERMINAL_SECONDARY_BUTTON_TEXT,
  ENTITY_NOTICE_TERMINAL_TITLE,
  persistEntityNoticeInterest,
} from '../../experiments/entity-notice-fakedoor'
import { LoginCard } from '../login-card'
import styles from './entity-notice-terminal.module.scss'

const PERSIST_SOURCE = 'entity-notice-terminal'

export interface EntityNoticeTerminalProps {
  email: string
  entityId: string | null
  variant: EntityNoticeVariant | null
  pageKind: EntityNoticePageKind | null
  onSeeMyLawsuits: () => void
  onClose?: () => void
}

/**
 * "Interesse registrado!" — the in-modal terminal of the entity-notice
 * fake-door signup (cells A/B). R2: only "Ver meus processos" reloads the
 * parent (the new session needs to appear); "Fechar" just closes the modal.
 *
 * QA2-8c: reaching this terminal means the account was created (8b) *and*
 * the visitor registered interest in a specific entity — additive, not an
 * alternative. persist fires once per mount; a failure to emit must never
 * block or break this UI (fire-and-forget).
 */
export function EntityNoticeTerminal({
  email,
  entityId,
  variant,
  pageKind,
  onSeeMyLawsuits,
  onClose,
}: EntityNoticeTerminalProps) {
  const hasPersistedRef = useRef(false)

  useEffect(() => {
    if (hasPersistedRef.current || !entityId || !variant) return
    hasPersistedRef.current = true
    persistEntityNoticeInterest({
      email,
      entityId,
      variant,
      pageKind,
      source: PERSIST_SOURCE,
    }).catch(() => undefined)
  }, [email, entityId, variant, pageKind])

  return (
    <LoginCard
      header={{ align: 'center', title: ENTITY_NOTICE_TERMINAL_TITLE }}
      onCloseClick={onClose}
    >
      <div className={styles.container}>
        <BodyText size="lg" className={styles.subtitle}>
          {buildEntityNoticeTerminalSubtitle(email)}
        </BodyText>
        <Button block onClick={onSeeMyLawsuits}>
          {ENTITY_NOTICE_TERMINAL_PRIMARY_BUTTON_TEXT}
        </Button>
        <Button kind="plain" block onClick={onClose}>
          {ENTITY_NOTICE_TERMINAL_SECONDARY_BUTTON_TEXT}
        </Button>
      </div>
    </LoginCard>
  )
}
